import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { moneyAmountLabel } from '@/utils/formatCurrency';
import { adsApi } from '@/api/client';
import { AdGroupFields } from '@/components/campaign-create/AdGroupFields';
import { KeywordFields } from '@/components/campaign-create/KeywordFields';
import { FormActions } from '@/components/campaign-create/CreateSpCampaignForm';
import {
  campaignBudgetMin,
  defaultAdGroupName,
  defaultCampaignBudgetInput,
  defaultCampaignDateRange,
  extractAdGroupMutationResults,
  extractSbKeywordCreateResults,
  extractSpSbCampaignMutateMessages,
  formatCreateCampaignUpstreamError,
  parseBrandsPayload,
  validateCampaignBudget,
} from '@/utils/campaignCreate';

type SbBidStrategy =
  | 'MAXIMIZE_IMMEDIATE_SALES'
  | 'MAXIMIZE_NEW_TO_BRAND_CUSTOMERS';

function parsePlacementPct(raw: string): number | null {
  const n = parseFloat(raw);
  if (Number.isNaN(n) || n <= 0) return null;
  return Math.round(n);
}

export function CreateSbCampaignForm({
  profileId,
  onSuccess,
  onCancel,
}: {
  profileId: string;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [campaignState, setCampaignState] = useState<'ENABLED' | 'PAUSED'>('ENABLED');
  const [productLocation, setProductLocation] = useState('SOLD_ON_AMAZON');
  const [startDate, setStartDate] = useState('');
  const [budget, setBudget] = useState(() => defaultCampaignBudgetInput('SB'));
  const [brandEntityId, setBrandEntityId] = useState('');
  const [bidOptimization, setBidOptimization] = useState(false);
  const [bidOptimizationStrategy, setBidOptimizationStrategy] =
    useState<SbBidStrategy>('MAXIMIZE_IMMEDIATE_SALES');
  const [homePct, setHomePct] = useState('');
  const [detailPagePct, setDetailPagePct] = useState('');
  const [otherPct, setOtherPct] = useState('');
  const [newToBrandPct, setNewToBrandPct] = useState('');
  const [adGroupName, setAdGroupName] = useState('');
  const [adGroupNameTouched, setAdGroupNameTouched] = useState(false);
  const [keywordText, setKeywordText] = useState('');
  const [matchType, setMatchType] = useState<'exact' | 'phrase' | 'broad'>('broad');
  const [keywordBid, setKeywordBid] = useState('1');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const budgetMin = campaignBudgetMin('SB');
  const qc = useQueryClient();

  const brandsQuery = useQuery({
    queryKey: ['profile-brands', profileId],
    queryFn: async () => {
      const res = await adsApi.getBrands(profileId);
      if (!res.success) throw new Error(res.message || 'Failed to load brands');
      return parseBrandsPayload(res.data);
    },
  });

  const brands = brandsQuery.data ?? [];

  useEffect(() => {
    const { startDate: s } = defaultCampaignDateRange();
    setName('');
    setCampaignState('ENABLED');
    setProductLocation('SOLD_ON_AMAZON');
    setStartDate(s);
    setBudget(defaultCampaignBudgetInput('SB'));
    setBrandEntityId('');
    setBidOptimization(false);
    setBidOptimizationStrategy('MAXIMIZE_IMMEDIATE_SALES');
    setHomePct('');
    setDetailPagePct('');
    setOtherPct('');
    setNewToBrandPct('');
    setAdGroupName('');
    setAdGroupNameTouched(false);
    setKeywordText('');
    setMatchType('broad');
    setKeywordBid('1');
    setSubmitError(null);
  }, []);

  useEffect(() => {
    if (brands.length === 1 && !brandEntityId) {
      setBrandEntityId(brands[0].brandEntityId);
    }
  }, [brands, brandEntityId]);

  useEffect(() => {
    if (!adGroupNameTouched) {
      setAdGroupName(defaultAdGroupName(name));
    }
  }, [name, adGroupNameTouched]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const trimmed = name.trim();
    if (!trimmed) {
      setSubmitError('Campaign name is required.');
      return;
    }
    const budgetNum = parseFloat(budget);
    const budgetError = validateCampaignBudget(budgetNum, 'SB', 'DAILY');
    if (budgetError) {
      setSubmitError(budgetError);
      return;
    }
    if (!startDate) {
      setSubmitError('Start date is required.');
      return;
    }
    if (brands.length > 0 && !brandEntityId) {
      setSubmitError('Brand is required for seller accounts.');
      return;
    }
    const trimmedAdGroupName = adGroupName.trim();
    if (!trimmedAdGroupName) {
      setSubmitError('Ad group name is required.');
      return;
    }
    const trimmedKeyword = keywordText.trim();
    let keywordBidNum: number | null = null;
    if (trimmedKeyword) {
      keywordBidNum = parseFloat(keywordBid);
      if (Number.isNaN(keywordBidNum) || keywordBidNum <= 0) {
        setSubmitError('Keyword bid must be greater than 0.');
        return;
      }
    }

    const bidding: Record<string, unknown> = {
      bidOptimization,
    };
    if (bidOptimizationStrategy) {
      bidding.bidOptimizationStrategy = bidOptimizationStrategy;
    }
    if (!bidOptimization) {
      const placements: Array<{ placement: string; percentage: number }> = [];
      const home = parsePlacementPct(homePct);
      const detail = parsePlacementPct(detailPagePct);
      const other = parsePlacementPct(otherPct);
      if (home !== null) placements.push({ placement: 'HOME', percentage: home });
      if (detail !== null) placements.push({ placement: 'DETAIL_PAGE', percentage: detail });
      if (other !== null) placements.push({ placement: 'OTHER', percentage: other });
      if (placements.length > 0) {
        bidding.bidAdjustmentsByPlacement = placements;
      }
      const ntb = parsePlacementPct(newToBrandPct);
      if (ntb !== null) {
        bidding.bidAdjustmentsByShopperSegment = [
          { shopperSegment: 'NEW_TO_BRAND_PURCHASE', percentage: ntb },
        ];
      }
    }

    const campaignPayload: Record<string, unknown> = {
      budgetType: 'DAILY',
      name: trimmed,
      state: campaignState,
      productLocation,
      startDate,
      budget: budgetNum,
      bidding,
    };
    if (brandEntityId) {
      campaignPayload.brandEntityId = brandEntityId;
    }

    setSubmitting(true);
    try {
      const res = await adsApi.createSbCampaigns(profileId, { campaigns: [campaignPayload] });
      if (!res.success) {
        setSubmitError(res.message || 'Failed to create campaign');
        return;
      }
      const { errors, successIds } = extractSpSbCampaignMutateMessages(res.data);
      if (errors.length > 0) {
        setSubmitError(errors.join(' '));
        return;
      }
      const campaignId = successIds[0];
      if (!campaignId) {
        setSubmitError('Campaign was created but no campaign ID was returned.');
        return;
      }

      const agRes = await adsApi.createSbAdGroups(profileId, {
        adGroups: [
          {
            campaignId: campaignId.toString(),
            name: trimmedAdGroupName,
            state: 'ENABLED',
          },
        ],
      });
      if (!agRes.success) {
        setSubmitError(
          `Campaign created, but ad group failed: ${agRes.message || 'Unknown error'}`
        );
        await qc.invalidateQueries({ queryKey: ['profile-campaigns', profileId] });
        return;
      }
      const agParsed = extractAdGroupMutationResults(agRes.data);
      if (agParsed.errors.length > 0) {
        setSubmitError(`Campaign created, but ad group failed: ${agParsed.errors.join(' ')}`);
        await qc.invalidateQueries({ queryKey: ['profile-campaigns', profileId] });
        return;
      }
      const adGroupId = agParsed.successIds[0];
      if (!adGroupId) {
        setSubmitError('Campaign created, but ad group ID was not returned.');
        await qc.invalidateQueries({ queryKey: ['profile-campaigns', profileId] });
        return;
      }

      if (trimmedKeyword && keywordBidNum !== null) {
        const kwRes = await adsApi.createSbKeywords(profileId, [
          {
            campaignId: campaignId.toString(),
            adGroupId: adGroupId.toString(),
            keywordText: trimmedKeyword,
            matchType,
            bid: keywordBidNum,
            state: 'enabled',
          },
        ]);
        if (!kwRes.success) {
          setSubmitError(
            `Campaign and ad group created, but keyword failed: ${kwRes.message || 'Unknown error'}`
          );
          await qc.invalidateQueries({ queryKey: ['profile-campaigns', profileId] });
          await qc.invalidateQueries({ queryKey: ['sb-ad-groups', profileId] });
          return;
        }
        const kwParsed = extractSbKeywordCreateResults(kwRes.data);
        if (kwParsed.errors.length > 0) {
          setSubmitError(
            `Campaign and ad group created, but keyword failed: ${kwParsed.errors.join(' ')}`
          );
          await qc.invalidateQueries({ queryKey: ['profile-campaigns', profileId] });
          await qc.invalidateQueries({ queryKey: ['sb-ad-groups', profileId] });
          return;
        }
      }

      await qc.invalidateQueries({ queryKey: ['profile-campaigns', profileId] });
      await qc.invalidateQueries({ queryKey: ['sb-ad-groups', profileId] });
      if (trimmedKeyword) {
        await qc.invalidateQueries({ queryKey: ['sb-keywords', profileId] });
      }
      onSuccess();
    } catch (err) {
      setSubmitError(formatCreateCampaignUpstreamError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <p className="text-sm text-foreground-muted mb-4">
        Creates a Sponsored Brands campaign, ad group, and optional keyword via Amazon APIs.
      </p>
      {submitError && (
        <div className="mb-4 text-sm text-error bg-error-subtle border border-error/20 rounded-xl px-3 py-2">
          {submitError}
        </div>
      )}
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground-muted mb-1">Campaign name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full border border-border rounded-lg px-3 py-2"
            disabled={submitting}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-foreground-muted mb-1">State</label>
            <select
              value={campaignState}
              onChange={(e) => setCampaignState(e.target.value as 'ENABLED' | 'PAUSED')}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm"
              disabled={submitting}
            >
              <option value="ENABLED">Enabled</option>
              <option value="PAUSED">Paused</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-muted mb-1">Product location</label>
            <select
              value={productLocation}
              onChange={(e) => setProductLocation(e.target.value)}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm"
              disabled={submitting}
            >
              <option value="SOLD_ON_AMAZON">Sold on Amazon</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-foreground-muted mb-1">Start date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm"
              disabled={submitting}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-muted mb-1">{moneyAmountLabel('Daily budget')}</label>
            <input
              type="number"
              step="0.01"
              min={budgetMin ?? undefined}
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              className="w-full border border-border rounded-lg px-3 py-2"
              disabled={submitting}
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground-muted mb-1">Brand</label>
          {brandsQuery.isLoading ? (
            <p className="text-sm text-foreground-subtle">Loading brands…</p>
          ) : brands.length > 0 ? (
            <select
              value={brandEntityId}
              onChange={(e) => setBrandEntityId(e.target.value)}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm"
              disabled={submitting}
              required
            >
              <option value="">Select a brand</option>
              {brands.map((b) => (
                <option key={b.brandEntityId} value={b.brandEntityId}>
                  {b.brandRegistryName || b.brandEntityId}
                </option>
              ))}
            </select>
          ) : (
            <>
              <input
                type="text"
                value={brandEntityId}
                onChange={(e) => setBrandEntityId(e.target.value)}
                className="w-full border border-border rounded-lg px-3 py-2"
                disabled={submitting}
                placeholder="ENTITY… (vendor accounts)"
              />
              <p className="text-xs text-foreground-subtle mt-1">
                Required for seller accounts. Leave empty only if your account does not require a brand entity.
              </p>
            </>
          )}
        </div>

        <fieldset className="border border-border rounded-xl p-4 space-y-3">
          <legend className="text-sm font-medium text-foreground-muted px-1">Bidding</legend>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={bidOptimization}
              onChange={(e) => setBidOptimization(e.target.checked)}
              disabled={submitting}
            />
            Enable bid optimization (Amazon-managed bidding)
          </label>
          <div>
            <label className="block text-sm font-medium text-foreground-muted mb-1">Bid optimization strategy</label>
            <select
              value={bidOptimizationStrategy}
              onChange={(e) => setBidOptimizationStrategy(e.target.value as SbBidStrategy)}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm"
              disabled={submitting}
            >
              <option value="MAXIMIZE_IMMEDIATE_SALES">Maximize immediate sales</option>
              <option value="MAXIMIZE_NEW_TO_BRAND_CUSTOMERS">Maximize new-to-brand customers</option>
            </select>
          </div>
          {!bidOptimization && (
            <>
              <p className="text-xs text-foreground-subtle">Placement bid adjustments (%)</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-foreground-muted mb-1">Home</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={homePct}
                    onChange={(e) => setHomePct(e.target.value)}
                    className="w-full border border-border rounded-lg px-2 py-1.5 text-sm"
                    disabled={submitting}
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label className="block text-xs text-foreground-muted mb-1">Detail page</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={detailPagePct}
                    onChange={(e) => setDetailPagePct(e.target.value)}
                    className="w-full border border-border rounded-lg px-2 py-1.5 text-sm"
                    disabled={submitting}
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label className="block text-xs text-foreground-muted mb-1">Other</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={otherPct}
                    onChange={(e) => setOtherPct(e.target.value)}
                    className="w-full border border-border rounded-lg px-2 py-1.5 text-sm"
                    disabled={submitting}
                    placeholder="Optional"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-muted mb-1">
                  New-to-brand shopper segment adjustment (%)
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={newToBrandPct}
                  onChange={(e) => setNewToBrandPct(e.target.value)}
                  className="w-full border border-border rounded-lg px-3 py-2"
                  disabled={submitting}
                  placeholder="Optional"
                />
              </div>
            </>
          )}
        </fieldset>

        <AdGroupFields
          adGroupName={adGroupName}
          onAdGroupNameChange={(value) => {
            setAdGroupNameTouched(true);
            setAdGroupName(value);
          }}
          disabled={submitting}
        />

        <KeywordFields
          keywordText={keywordText}
          onKeywordTextChange={setKeywordText}
          matchType={matchType}
          onMatchTypeChange={(value) => setMatchType(value as 'exact' | 'phrase' | 'broad')}
          bid={keywordBid}
          onBidChange={setKeywordBid}
          matchTypeOptions={[
            { value: 'exact', label: 'Exact' },
            { value: 'phrase', label: 'Phrase' },
            { value: 'broad', label: 'Broad' },
          ]}
          disabled={submitting}
        />

        <FormActions submitting={submitting} onCancel={onCancel} />
      </form>
    </>
  );
}
